<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>Get Involved</h2>
        <p>Join us in empowering the next generation of innovators</p>
      </div>
    </section>

    <section class="content-section">
      <div class="container">
        <h2>Make a Difference</h2>
        <p>There are many ways to support Space Coast STEM and help students in Brevard County gain access to technology and STEM education. Whether you have time, resources, or expertise to share, we'd love to have you join our mission.</p>

        <div class="program-grid">
          <div class="program-card">
            <h3>Volunteer Your Time</h3>
            <p>Help us refurbish computers, mentor students, or assist with events. We need volunteers with all kinds of skills and backgrounds.</p>
            <p><strong>Opportunities:</strong></p>
            <ul>
              <li>Computer refurbishment technicians</li>
              <li>Mentors for students</li>
              <li>Event coordinators and support staff</li>
              <li>Administrative assistance</li>
            </ul>
            <p style="margin-top: 1rem;">
              <router-link to="/volunteer" class="btn btn-primary">Sign Up as a Volunteer</router-link>
            </p>
          </div>

          <div class="program-card">
            <h3>Support Our Mission</h3>
            <p>You can help us provide more laptops to students in need through financial contributions or equipment donations.</p>
            <p style="margin-top: 1rem;">
              <router-link to="/donate" class="btn btn-primary">Learn About Donating</router-link>
            </p>
          </div>

          <div class="program-card">
            <h3>Corporate Partnerships</h3>
            <p>Businesses can support Space Coast STEM through sponsorships, employee giving programs, equipment donations, or by hosting student field trips and job shadowing opportunities.</p>
            <p><strong>Partnership benefits:</strong></p>
            <ul>
              <li>Community impact and visibility</li>
              <li>Employee engagement opportunities</li>
              <li>Tax-deductible contributions</li>
              <li>Connection with future workforce</li>
            </ul>
          </div>

          <div class="program-card">
            <h3>School Partnerships</h3>
            <p>We work closely with schools throughout Brevard County to identify students who would benefit from our laptop programs.</p>
            <p><strong>For educators:</strong></p>
            <ul>
              <li>Refer students to our programs</li>
              <li>Collaborate on STEM initiatives</li>
              <li>Sign up for portal access to manage requests</li>
            </ul>
            <p style="margin-top: 1rem;">
              <a href="https://portal.spacecoaststem.org/login?signup=true" class="btn btn-primary" target="_blank" rel="noopener noreferrer">Teacher Portal Sign Up</a>
            </p>
          </div>

          <div class="program-card">
            <h3>Spread the Word</h3>
            <p>Help us reach more students and supporters by sharing our mission with your network, on social media, and in your community.</p>
            <p><strong>How to help:</strong></p>
            <ul>
              <li>Share our website and programs</li>
              <li>Tell families about our services</li>
              <li>Connect us with potential donors</li>
              <li>Attend and promote our events</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <section class="content-section requirements-section">
      <div class="container">
        <h2>Volunteer Requirements</h2>
        <p>All volunteers who work directly with students must:</p>
        <ul>
          <li>Complete a volunteer application</li>
          <li>Pass a background check</li>
          <li>Attend a brief orientation session</li>
          <li>Commit to a minimum time period (varies by role)</li>
        </ul>
        <p>We provide training and support for all volunteer positions. No prior experience with technology or education is required for many roles - just enthusiasm and a desire to help students succeed!</p>
      </div>
    </section>

    <section class="cta">
      <div class="container">
        <h2>Ready to Get Started?</h2>
        <p>Contact us to learn more about volunteer opportunities, donations, or partnerships</p>
        <div class="cta-buttons">
          <router-link to="/volunteer" class="btn btn-primary">Volunteer Today</router-link>
          <router-link to="/contact" class="btn btn-secondary">Contact Us</router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
// Get Involved view - static content
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  opacity: 0.95;
}

.content-section {
  padding: 5rem 2rem;
}

.content-section h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.content-section p {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-light, #666666);
  margin-bottom: 1rem;
}

.requirements-section {
  background: var(--light-bg, #f5f7fa);
}

.program-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.program-card {
  background: var(--white, #ffffff);
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s;
}

.program-card:hover {
  transform: translateY(-5px);
}

.program-card h3 {
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1rem;
  margin-top: 0;
  font-family: 'Poppins', sans-serif;
}

.program-card ul {
  margin-left: 1.5rem;
  line-height: 1.8;
}

.cta {
  background: var(--dark-bg, #1f3447);
  color: var(--white, #ffffff);
  padding: 5rem 2rem;
  text-align: center;
}

.cta h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
}

.cta p {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.btn {
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.btn-primary {
  background: var(--accent-color, #e94f3a);
  color: var(--white, #ffffff);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(233, 79, 58, 0.3);
}

.btn-secondary {
  border-color: var(--white, #ffffff);
  background: transparent;
  color: var(--white, #ffffff);
}

.btn-secondary:hover {
  background: var(--white, #ffffff);
  color: var(--primary-color, #2c4a6b);
}

@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .content-section h2, .cta h2 {
    font-size: 2rem;
  }

  .program-grid {
    grid-template-columns: 1fr;
  }
}
</style>
